import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { UpdateApprovedAmountDtoOut } from '@/models/out/UpdateApprovedAmountDtoOut';
import { UpdateSupportRequestStatusDtoOut } from '@/models/out/UpdateSupportRequestStatusDtoOut';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import { queryKeys } from '@/hooks/QueryKeys';
import Button from '@/components/ui/Button';
import SupportRequestType from '@/models/common/SupportRequestType';
import AllowedFor from '@/models/common/AllowedFor';
import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import { getSupplierDisplayDetails } from '@/helpers/supplierDisplayHelper';
import { getApproveStatus, getMoveToExecutionStatus } from '@/helpers/supportRequestDecision';
import SupplierApprovalGate from './SupplierApprovalGate/SupplierApprovalGate';
import styles from './ApprovedAmountDialog.module.css';

interface ApprovedAmountDialogProps {
  open: boolean;
  onClose: () => void;
  request: ManagementSupportRequestDtoIn;
  onSuccess: () => void;
}

const ApprovedAmountDialog = ({
  open,
  onClose,
  request,
  onSuccess,
}: ApprovedAmountDialogProps) => {
  const {
    multiRequestCorrelationId: correlationId,
    multiRequestCounter: counter,
    isInInitialStatusProcess,
    requestedAmount,
    approvedAmount: currentApprovedAmount,
    type,
    beneficiaryContact,
    fundBalance,
    goal,
    supplier,
    additionalDetails,
  } = request;
  const { t, i18n } = useTranslation();
  const [approvedAmount, setApprovedAmount] = useState<string>('');
  const [applyToOthers, setApplyToOthers] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // The supplier approval gate runs before anything is written, so backing out of it leaves
  // both the amount and the status untouched.
  const [gateOpen, setGateOpen] = useState(false);
  // True from the gate's "continue" until the approval finishes, so the spinner lands on the
  // button that was pressed even while the amount half of the flow is running.
  const [isApproveFlow, setIsApproveFlow] = useState(false);

  const isMonthly = type === SupportRequestType.Monthly;
  // The second button approves a request still awaiting a decision, or sends an already-approved
  // month (the "Approved" tab) on to execution.
  const approveStatus = getApproveStatus(request) ?? getMoveToExecutionStatus(request);
  const isToExecution = approveStatus === 'PendingExecution';

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString(i18n.language, {
      style: 'currency',
      currency: 'ILS',
    });
  };

  const { mutateAsync: updateApprovedAmount, isPending: isSavingAmount } = usePutAuthApi<void, object, UpdateApprovedAmountDtoOut>(
    'management/support-requests/approved-amount'
  );
  const { mutateAsync: updateStatus, isPending: isApproving } = usePutAuthApi<void, object, UpdateSupportRequestStatusDtoOut>(
    'management/support-requests/status'
  );
  const isSubmitting = isSavingAmount || isApproving;

  // Initialize approved amount when dialog opens
  useEffect(() => {
    if (open) {
      setApprovedAmount(currentApprovedAmount.toString());
      setApplyToOthers(false);
      setComment('');
      setError(null);
      setGateOpen(false);
    }
  }, [open, currentApprovedAmount, requestedAmount]);

  const validateAmount = (value: number): string | null => {
    if (isNaN(value)) {
      return t('invalidAmount');
    }
    
    if (value < 0) {
      return t('amountCannotBeNegative');
    }
    
    return null;
  };

  const getScopeOptions = (): { value: boolean; label: string }[] => {
    if (!isMonthly) {
      return [];
    }

    if (isInInitialStatusProcess) {
      return [
        { value: false, label: t('editRequestedForDate.firstOnly') },
        { value: true, label: t('editRequestedForDate.all') },
      ];
    }

    return [
      { value: false, label: t('editRequestedForDate.thisOnly') },
      { value: true, label: t('editRequestedForDate.fromHereOnward') },
    ];
  };

  const scopeOptions = getScopeOptions();

  const supportedName = (() => {
    if (goal?.allowedFor === AllowedFor.Fund) {
      const details = additionalDetails as FundExpenseDetails | undefined;
      const supplierDisplay = getSupplierDisplayDetails(supplier, additionalDetails);
      return details?.isAlreadyPaid ? details?.payerName : supplierDisplay?.name;
    }
    return beneficiaryContact ? `${beneficiaryContact.firstName} ${beneficiaryContact.lastName}` : undefined;
  })();

  const dialogTitle = supportedName
    ? t('changeApprovedAmountWithSupportedName', { name: supportedName })
    : t('changeApprovedAmount');

  // The amount + scope already written by an approve attempt whose status change then failed, so a
  // retry doesn't write the amount (and its note) a second time.
  const savedAmountKey = useRef<string | null>(null);

  const isValidAmount = (value: number): boolean => {
    const validationError = validateAmount(value);
    setError(validationError);
    return !validationError;
  };

  const saveAmount = (newAmount: number, trimmedComment: string | undefined) => {
    const body: UpdateApprovedAmountDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : (counter ?? null),
      isInInitialStatusProcess,
      approvedAmount: newAmount,
      applyToOthers: isMonthly ? applyToOthers : false,
      comment: trimmedComment,
    };
    return updateApprovedAmount({ body });
  };

  const handleSaveAmount = async () => {
    const newAmount = parseFloat(approvedAmount);

    if (newAmount === currentApprovedAmount && !applyToOthers) {
      handleClose();
      return;
    }

    if (!isValidAmount(newAmount)) {
      return;
    }

    try {
      await saveAmount(newAmount, comment.trim() || undefined);
    } catch (err) {
      setError(t('errorUpdatingApprovedAmount') + ': ' + (err as Error).message);
      return;
    }
    onSuccess();
    handleClose();
  };

  const handleApproveClick = () => {
    if (isValidAmount(parseFloat(approvedAmount))) {
      setGateOpen(true);
    }
  };

  const performApprove = async () => {
    if (!approveStatus) return;
    setIsApproveFlow(true);
    try {
      await saveAmountAndApprove(approveStatus);
    } finally {
      setIsApproveFlow(false);
    }
  };

  const saveAmountAndApprove = async (newStatus: string) => {
    const newAmount = parseFloat(approvedAmount);
    const trimmedComment = comment.trim() || undefined;
    const amountKey = `${newAmount}|${applyToOthers}`;
    const amountChanged = (newAmount !== currentApprovedAmount || applyToOthers)
      && savedAmountKey.current !== amountKey;

    if (amountChanged) {
      try {
        await saveAmount(newAmount, trimmedComment);
      } catch (err) {
        setError(t('errorUpdatingApprovedAmount') + ': ' + (err as Error).message);
        return;
      }
    }

    const body: UpdateSupportRequestStatusDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : (counter ?? null),
      isInInitialStatusProcess,
      newStatus,
      // The note travels with the amount change when there is one; otherwise it goes on the
      // approval, internal like the amount note.
      comment: amountChanged ? undefined : trimmedComment,
      showInPersonalArea: false,
      sendEmail: false,
    };

    try {
      await updateStatus({ body });
    } catch {
      if (amountChanged) {
        savedAmountKey.current = amountKey;
        setComment('');
        // Show the saved amount in the table behind the dialog.
        onSuccess();
      }
      const savedButFailedKey = isToExecution
        ? 'approvedAmountSavedButExecutionFailed'
        : 'approvedAmountSavedButApprovalFailed';
      setError(amountChanged || savedAmountKey.current ? t(savedButFailedKey) : t('errorUpdatingStatus'));
      return;
    }

    onSuccess();
    // Approval moves the row to another tab and changes the fund's balances.
    queryClient.refetchQueries({
      predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
    });
    queryClient.invalidateQueries({ predicate: (query) => queryKeys.fundBalance.isBalanceQuery(query.queryKey) });
    handleClose();
  };

  // The gate re-runs its continuation whenever onContinue changes identity, and closing it here
  // re-renders this dialog - so the handler must stay stable or the approval is sent twice.
  const performApproveRef = useRef(performApprove);
  performApproveRef.current = performApprove;
  const handleGateContinue = useCallback(() => {
    setGateOpen(false);
    performApproveRef.current();
  }, []);
  const handleGateCancel = useCallback(() => setGateOpen(false), []);

  const handleClose = () => {
    setApprovedAmount('');
    setComment('');
    setError(null);
    setGateOpen(false);
    savedAmountKey.current = null;
    onClose();
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApprovedAmount(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  return (
    <>
    {approveStatus && (
      <SupplierApprovalGate
        open={gateOpen}
        selectedRequests={[request]}
        newStatus={approveStatus}
        onContinue={handleGateContinue}
        onCancel={handleGateCancel}
      />
    )}
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      title={dialogTitle}
    >
      <div className={styles.content}>
        <div className={styles.subtitleRow}>
          <span className={styles.subtitle}>
            {t('requestedAmount')}: {formatCurrency(requestedAmount)}
          </span>
          <span className={styles.subtitle}>
            {t('fundBalance')}: {formatCurrency(fundBalance)}
          </span>
        </div>

        <div className={styles.inputSection}>
          <label htmlFor="approvedAmount" className={styles.inputLabel}>
            {t('approvedAmount')}:
          </label>
          <input
            id="approvedAmount"
            type="number"
            className={`${styles.amountInput} ${error ? styles.hasError : ''}`}
            value={approvedAmount ?? ''}
            onChange={handleAmountChange}
            min={0}
          />
        </div>

        {scopeOptions.length > 0 && (
          <div className={styles.scopeSection}>
            <label className={styles.scopeLabel}>{t('approvedAmountScope')}:</label>
            <div className={styles.radioGroup}>
              {scopeOptions.map((option, index) => (
                <label key={index} className={styles.radioOption}>
                  <input
                    type="radio"
                    name="scope"
                    checked={applyToOthers === option.value}
                    onChange={() => setApplyToOthers(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className={styles.noteSection}>
          <label htmlFor="note" className={styles.inputLabel}>
            {t('note')}
          </label>
          <textarea
            id="note"
            className={styles.noteInput}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('supportRequestsManagement.notePlaceholder')}
            rows={3}
          />
        </div>

        {error && (
          <p className={styles.error}>{error}</p>
        )}

        <div className={styles.actions}>
          <Button
            variant="white"
            className={styles.secondaryButton}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('close')}
          </Button>
          <Button
            variant={approveStatus ? 'white' : 'primary'}
            className={approveStatus ? styles.secondaryButton : styles.actionButton}
            onClick={handleSaveAmount}
            disabled={isSubmitting || gateOpen}
          >
            {isSavingAmount && !isApproveFlow ? t('loading') : t('saveApprovedAmount')}
          </Button>
          {approveStatus && (
            <Button
              variant="primary"
              className={styles.actionButton}
              onClick={handleApproveClick}
              disabled={isSubmitting || gateOpen}
            >
              {isApproveFlow ? t('loading') : t(isToExecution ? 'saveAndMoveToExecution' : 'saveAndApproveSupport')}
            </Button>
          )}
        </div>
      </div>
    </MainDialogLayout>
    </>
  );
};

export default ApprovedAmountDialog;
